import { createTheme } from "@mui/material/styles";

// Default brand colors - can be overridden by organization branding
const defaultBranding = {
  primary: "#427674", // Mighty45 teal
  primaryLight: "#a9e3d7",
  primaryText: "#ffffff",
  primaryLightText: "#000000",
  secondary: "#2e7d32",
  secondaryLight: "#60ad5e",
  secondaryText: "#ffffff",
  secondaryLightText: "#000000",
};

/**
 * Helper function to determine if a color is light or dark
 */
const getBrightness = (hexColor) => {
  if (!hexColor || !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hexColor))
    return 128;

  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);

  return (r * 299 + g * 587 + b * 114) / 1000;
};

/**
 * Get appropriate text color for a background
 */
const getTextColor = (backgroundColor) => {
  return getBrightness(backgroundColor) > 140 ? "#000000" : "#ffffff";
};

/**
 * Generate smart color variants from organization branding
 */
const generateColorSystem = (branding) => {
  const primary = branding.primary || defaultBranding.primary;
  const secondary = branding.secondary || defaultBranding.secondary;

  // Generate meal type colors using organization branding as base
  const mealTypeColors = {
    breakfast: branding.primaryLight || adjustColorBrightness(primary, 40),
    lunch: branding.secondary || secondary,
    dinner: branding.primary || primary,
    snack: branding.secondaryLight || adjustColorBrightness(secondary, 40),
  };

  // Generate ingredient category colors
  const ingredientColors = {
    Proteins: branding.secondary || secondary,
    Vegetables: adjustColorHue(primary, 80), // Green-ish
    Fruits: adjustColorHue(primary, 30), // Orange-ish
    Grains: adjustColorHue(secondary, -30), // Brown-ish
    Dairy: branding.primary || primary,
    Pantry: branding.secondaryLight || adjustColorBrightness(secondary, 30),
    Other: adjustColorBrightness(primary, -20), // Darker variant
  };

  return {
    mealTypeColors,
    ingredientColors,
    progressColor: branding.secondary || secondary,
    successColor:
      branding.secondaryLight || adjustColorBrightness(secondary, 40),
    cardBackground: "#ffffff",
    lightBackground: "#fafafa",
    greyBackground: "#f5f5f5",
  };
};

/**
 * Adjust color brightness
 */
const adjustColorBrightness = (hex, percent) => {
  if (!hex || !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex)) return hex;

  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00ff) + amt;
  const B = (num & 0x0000ff) + amt;

  return (
    "#" +
    (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
  );
};

/**
 * Adjust color hue
 */
const adjustColorHue = (hex, degrees) => {
  if (!hex || !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex)) return hex;

  // Simple hue adjustment - this is a basic implementation
  // In a production app, you might want to use a proper color manipulation library
  const hueShift = Math.abs(degrees) % 60; // Keep shifts reasonable

  // Return a reasonable color variant based on hue shift
  if (degrees > 0) {
    return adjustColorBrightness(hex, hueShift > 30 ? 20 : -10);
  } else {
    return adjustColorBrightness(hex, hueShift > 30 ? -20 : 10);
  }
};

/**
 * Creates a custom theme for the client meal plan viewer
 * Incorporates organization branding colors when available
 */
export const createClientTheme = (organizationBranding = null) => {
  const branding = organizationBranding || defaultBranding;
  const colorSystem = generateColorSystem(branding);

  // Ensure we have proper text colors
  const primaryTextColor =
    branding.primaryText || getTextColor(branding.primary);
  const secondaryTextColor =
    branding.secondaryText || getTextColor(branding.secondary);

  return createTheme({
    palette: {
      primary: {
        main: branding.primary,
        light: branding.primaryLight,
        contrastText: primaryTextColor,
      },
      secondary: {
        main: branding.secondary,
        light: branding.secondaryLight,
        contrastText: secondaryTextColor,
      },
      background: {
        default: colorSystem.lightBackground,
        paper: colorSystem.cardBackground,
      },
      text: {
        primary: "#1a1a1a",
        secondary: "#666666",
      },
      // Custom colors for meal plan components
      custom: {
        mealTypes: colorSystem.mealTypeColors,
        ingredients: colorSystem.ingredientColors,
        progress: colorSystem.progressColor,
        success: colorSystem.successColor,
        grey: colorSystem.greyBackground,
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: "2.5rem",
        fontWeight: 700,
        lineHeight: 1.2,
      },
      h2: {
        fontSize: "2rem",
        fontWeight: 600,
        lineHeight: 1.3,
      },
      h3: {
        fontSize: "1.5rem",
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h4: {
        fontSize: "1.25rem",
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: "1.125rem",
        fontWeight: 500,
        lineHeight: 1.4,
      },
      h6: {
        fontSize: "1rem",
        fontWeight: 500,
        lineHeight: 1.4,
      },
      body1: {
        fontSize: "1rem",
        lineHeight: 1.6,
      },
      body2: {
        fontSize: "0.875rem",
        lineHeight: 1.5,
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiMenu: {
        defaultProps: {
          disableScrollLock: true,
        },
      },
      MuiPopover: {
        defaultProps: {
          disableScrollLock: true,
        },
      },
      MuiModal: {
        defaultProps: {
          disableScrollLock: true,
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            borderRadius: 16,
            border: "1px solid rgba(0,0,0,0.06)",
            "&:hover": {
              boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 500,
            borderRadius: 8,
            padding: "8px 24px",
          },
          contained: {
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            "&:hover": {
              boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            fontWeight: 500,
          },
        },
      },
    },
  });
};

/**
 * Helper functions to get colors from the theme
 * These can be used by components to access the intelligent color system
 */
export const getMealTypeColor = (mealType, theme) => {
  if (theme?.palette?.custom?.mealTypes) {
    return theme.palette.custom.mealTypes[mealType] || theme.palette.grey[500];
  }

  // Fallback colors if theme not available
  const fallbackColors = {
    breakfast: "#FF9800",
    lunch: "#4CAF50",
    dinner: "#2196F3",
    snack: "#9C27B0",
  };
  return fallbackColors[mealType] || "#757575";
};

export const getIngredientCategoryColor = (category, theme) => {
  if (theme?.palette?.custom?.ingredients) {
    return (
      theme.palette.custom.ingredients[category] || theme.palette.grey[500]
    );
  }

  // Fallback colors if theme not available
  const fallbackColors = {
    Proteins: "#f44336",
    Vegetables: "#4caf50",
    Fruits: "#ff9800",
    Grains: "#795548",
    Dairy: "#2196f3",
    Pantry: "#9c27b0",
    Other: "#607d8b",
  };
  return fallbackColors[category] || "#757575";
};

export const getProgressColor = (theme) => {
  return (
    theme?.palette?.custom?.progress ||
    theme?.palette?.secondary?.main ||
    "#4CAF50"
  );
};

export const getSuccessColor = (theme) => {
  return (
    theme?.palette?.custom?.success ||
    theme?.palette?.secondary?.light ||
    "#60ad5e"
  );
};

export const getGreyBackground = (theme) => {
  return theme?.palette?.custom?.grey || "#f5f5f5";
};
